import {
  INGESTION_SOURCE_LABELS,
  LOT_STATUS_LABELS,
  type LotDetail,
  type LotProgress,
  PERMISSIONS,
  RECEIPT_STATUS_LABELS,
  type ReceiptSummary,
} from '@edms/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Archive,
  ArrowLeft,
  FileUp,
  Inbox,
  Loader2,
  ScanLine,
  UploadCloud,
} from 'lucide-react';
import { type DragEvent, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { PageBody, PageHeader } from '@/components/page-header';
import { type Column, DataTable } from '@/components/ui/data-table';
import { useToast } from '@/components/ui/overlays';
import { Button } from '@/components/ui/primitives';
import { Badge, Card, EmptyState, ProgressBar, SectionHeader, Skeleton, type Tone } from '@/components/ui/surfaces';
import { DocumentViewer } from '@/features/receipts/document-viewer';
import { useAuth } from '@/features/auth/auth-context';
import { api, ApiError } from '@/lib/api-client';
import { cn, formatDate, formatDateTime, formatNumber, formatPercent } from '@/lib/utils';
import { LOT_STATUS_TONES } from './lots-page';

const RECEIPT_TONES: Record<ReceiptSummary['status'], Tone> = {
  IMPORTED: 'neutral',
  PROCESSING: 'info',
  OCR_PENDING: 'info',
  NEEDS_REVIEW: 'warning',
  DUPLICATE_SUSPECTED: 'warning',
  READY: 'accent',
  ARCHIVED: 'success',
  REJECTED: 'danger',
};

/**
 * Poste de numérisation (§4, §5).
 *
 * Rassemble sur un seul écran ce qu'un opérateur enchaîne dans la journée :
 * déposer les fichiers, suivre le traitement, lancer la reconnaissance,
 * archiver. Le suivi se met à jour seul tant qu'un traitement est en cours —
 * l'opérateur ne doit pas avoir à rafraîchir pour savoir où il en est.
 */
export function LotDetailPage(): React.JSX.Element {
  const { lotId = '' } = useParams<{ lotId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const { can } = useAuth();

  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [openReceiptId, setOpenReceiptId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { data: lot, isLoading } = useQuery({
    queryKey: ['lot', lotId],
    queryFn: () => api.get<LotDetail>(`/lots/${lotId}`),
  });

  const { data: progress } = useQuery({
    queryKey: ['lot', lotId, 'progress'],
    queryFn: () => api.get<LotProgress>(`/lots/${lotId}/progress`),
    // Sondage tant que le lot travaille ; à l'arrêt, plus aucune requête.
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status && status !== 'ARCHIVED' && status !== 'ERROR' ? 4000 : false;
    },
  });

  const { data: receipts } = useQuery({
    queryKey: ['lot', lotId, 'receipts'],
    queryFn: () => api.get<ReceiptSummary[]>(`/receipts/by-lot/${lotId}`),
    refetchInterval: progress && progress.ocrPending > 0 ? 5000 : false,
  });

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['lot', lotId] });
    void queryClient.invalidateQueries({ queryKey: ['lots'] });
  };

  const runOcr = useMutation({
    mutationFn: () => api.post<LotProgress>(`/lots/${lotId}/run-ocr`),
    onSuccess: (result) => {
      toast.success('Reconnaissance lancée', `${result.totalReceipts} document(s) en file.`);
      invalidate();
    },
    onError: (error) =>
      toast.error('Lancement impossible', error instanceof ApiError ? error.message : undefined),
  });

  const archive = useMutation({
    mutationFn: () =>
      api.post<{ archived: number; skipped: number; failures: { reason: string }[] }>(
        `/lots/${lotId}/archive`,
      ),
    onSuccess: (result) => {
      if (result.failures.length > 0 || result.skipped > 0) {
        toast.notify({
          tone: 'warning',
          title: `${result.archived} document(s) validé(s) et archivé(s)`,
          description:
            result.skipped > 0
              ? `${result.skipped} document(s) restent à traiter : numéro illisible ou doublon à trancher.`
              : `${result.failures.length} en échec : ${result.failures[0]?.reason ?? ''}`,
        });
      } else {
        toast.success(
          'Lot archivé',
          `${result.archived} document(s) validés et versés aux archives.`,
        );
      }
      invalidate();
    },
    onError: (error) =>
      toast.error('Archivage impossible', error instanceof ApiError ? error.message : undefined),
  });

  const uploadFiles = async (files: FileList | null): Promise<void> => {
    if (!files || files.length === 0) return;

    const formData = new FormData();
    for (const file of Array.from(files)) formData.append('files', file);

    setUploading(true);
    try {
      const result = await api.upload<{
        acceptedFiles: number;
        rejectedFiles: { fileName: string; reason: string }[];
      }>(`/lots/${lotId}/files`, formData);

      if (result.rejectedFiles.length > 0) {
        toast.notify({
          tone: 'warning',
          title: `${result.acceptedFiles} fichier(s) accepté(s)`,
          description: `${result.rejectedFiles[0]?.fileName} : ${result.rejectedFiles[0]?.reason}`,
        });
      } else {
        toast.success(
          `${result.acceptedFiles} fichier(s) déposé(s)`,
          'Le traitement démarre en arrière-plan.',
        );
      }
      invalidate();
    } catch (error) {
      toast.error('Dépôt impossible', error instanceof ApiError ? error.message : undefined);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const onDrop = (event: DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    setDragging(false);
    void uploadFiles(event.dataTransfer.files);
  };

  if (isLoading || !lot) {
    return (
      <PageBody>
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-40 w-full" />
      </PageBody>
    );
  }

  const editable = lot.status !== 'ARCHIVED';
  const columns: readonly Column<ReceiptSummary>[] = [
    {
      key: 'orderNumber',
      header: 'Numéro',
      width: '20%',
      render: (row) => (
        <span className="font-mono text-sm text-ink">
          {row.orderNumber ?? row.searchKey ?? <span className="text-ink-faint">non reconnu</span>}
        </span>
      ),
    },
    {
      key: 'sub',
      header: 'Sous-n°',
      width: '12%',
      render: (row) => <span className="font-mono text-xs text-ink-muted">{row.subNumber ?? '—'}</span>,
    },
    {
      key: 'delivery',
      header: 'Livraison',
      width: '12%',
      render: (row) => <span className="text-sm text-ink-muted">{formatDate(row.deliveryDate)}</span>,
    },
    {
      key: 'driver',
      header: 'Chauffeur',
      hideOnNarrow: true,
      render: (row) => <span className="truncate text-sm text-ink-muted">{row.driverName ?? '—'}</span>,
    },
    {
      key: 'signature',
      header: 'Signature',
      align: 'center',
      width: '9%',
      render: (row) =>
        row.hasSignature ? <Badge tone="success">Oui</Badge> : <span className="text-xs text-ink-faint">Non</span>,
    },
    {
      key: 'confidence',
      header: 'OCR',
      align: 'right',
      width: '8%',
      render: (row) => (
        <span
          className={cn(
            'text-xs',
            row.ocrConfidence !== null && row.ocrConfidence < 0.8 ? 'text-warning' : 'text-ink-muted',
          )}
        >
          {formatPercent(row.ocrConfidence)}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Statut',
      align: 'right',
      width: '14%',
      render: (row) => <Badge tone={RECEIPT_TONES[row.status]}>{RECEIPT_STATUS_LABELS[row.status]}</Badge>,
    },
  ];

  return (
    <div className="flex h-full min-h-0">
      <div className="flex min-w-0 flex-1 flex-col">
        <PageHeader
          title={lot.reference}
          description={`${lot.branchCode} — ${lot.branchName} · ${INGESTION_SOURCE_LABELS[lot.source]} · ${lot.operatorName}`}
          actions={
            <>
              <Button
                variant="ghost"
                icon={<ArrowLeft className="size-4" />}
                onClick={() => navigate('/numerisation')}
              >
                Retour
              </Button>

              {editable && can(PERMISSIONS.LOTS_RUN_OCR) && (
                <Button
                  icon={<ScanLine className="size-4" />}
                  loading={runOcr.isPending}
                  onClick={() => runOcr.mutate()}
                >
                  Relancer l'OCR
                </Button>
              )}

              {editable && can(PERMISSIONS.LOTS_ARCHIVE) && (
                <Button
                  variant="primary"
                  icon={<Archive className="size-4" />}
                  loading={archive.isPending}
                  disabled={(progress?.totalReceipts ?? 0) === 0}
                  title="Valide tous les documents reconnus du lot et les verse aux archives"
                  onClick={() => archive.mutate()}
                >
                  Valider et archiver le lot
                </Button>
              )}
            </>
          }
        />

        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-6 py-5">
          <div className="grid gap-4 lg:grid-cols-3">
            <Card className="lg:col-span-2">
              <SectionHeader
                title="Avancement"
                actions={
                  <Badge tone={LOT_STATUS_TONES[lot.status]} dot>
                    {LOT_STATUS_LABELS[lot.status]}
                  </Badge>
                }
              />

              {progress && (
                <div className="mt-4 space-y-3">
                  <ProgressBar
                    value={progress.percentComplete}
                    tone={progress.status === 'ERROR' ? 'danger' : 'accent'}
                  />
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
                    <Metric label="Documents" value={formatNumber(progress.totalReceipts)} />
                    <Metric label="Prêts" value={formatNumber(progress.processedReceipts)} tone="success" />
                    <Metric label="OCR en file" value={formatNumber(progress.ocrPending)} tone="info" />
                    <Metric label="À valider" value={formatNumber(progress.needsReview)} tone="warning" />
                    <Metric label="Doublons" value={formatNumber(progress.duplicates)} tone="warning" />
                  </div>
                </div>
              )}

              {lot.errorMessage && (
                <p className="mt-4 rounded-md border border-danger-border bg-danger-soft px-3 py-2 text-xs text-danger">
                  {lot.errorMessage}
                </p>
              )}
            </Card>

            <Card>
              <SectionHeader title="Historique" description="Traçabilité des opérations" />
              <ol className="mt-4 space-y-3">
                {lot.events.slice(0, 8).map((event) => (
                  <li key={event.id} className="flex gap-2.5">
                    <span className="mt-1.5 size-1.5 shrink-0 rounded-full bg-accent" aria-hidden />
                    <div className="min-w-0">
                      <p className="text-xs font-medium text-ink">{event.label}</p>
                      <p className="text-2xs text-ink-faint">
                        {formatDateTime(event.occurredAt)}
                        {event.actorName ? ` · ${event.actorName}` : ''}
                      </p>
                      {event.detail && <p className="mt-0.5 text-2xs text-ink-muted">{event.detail}</p>}
                    </div>
                  </li>
                ))}
              </ol>
            </Card>
          </div>

          {editable && can(PERMISSIONS.LOTS_SCAN) && (
            <div
              onDragOver={(event) => {
                event.preventDefault();
                setDragging(true);
              }}
              onDragLeave={() => setDragging(false)}
              onDrop={onDrop}
              className={cn(
                'rounded-lg border-2 border-dashed px-6 py-8 text-center transition-colors',
                dragging
                  ? 'border-accent bg-accent-soft'
                  : 'border-line bg-raised hover:border-line-strong',
              )}
            >
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept=".pdf,.tif,.tiff,.png,.jpg,.jpeg"
                className="hidden"
                onChange={(event) => void uploadFiles(event.target.files)}
              />

              {uploading ? (
                <div className="flex flex-col items-center gap-2 text-ink-muted">
                  <Loader2 className="size-6 animate-spin" aria-hidden />
                  <p className="text-sm">Dépôt en cours…</p>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2">
                  <UploadCloud className="size-7 text-ink-faint" aria-hidden />
                  <p className="text-sm font-medium text-ink">
                    Déposez les fichiers numérisés ici
                  </p>
                  <p className="text-xs text-ink-faint">
                    PDF multipage, TIFF, PNG ou JPEG · le traitement démarre automatiquement
                  </p>
                  <Button
                    className="mt-2"
                    icon={<FileUp className="size-4" />}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    Parcourir…
                  </Button>
                </div>
              )}
            </div>
          )}

          <div className="overflow-hidden rounded-lg border border-line-subtle bg-raised shadow-[var(--shadow-card)]">
            <div className="border-b border-line-subtle px-4 py-2.5">
              <p className="text-xs text-ink-faint">
                <span className="tabular font-medium text-ink-muted">
                  {formatNumber(receipts?.length ?? 0)}
                </span>{' '}
                document(s) dans ce lot
              </p>
            </div>

            <DataTable
              columns={columns}
              rows={receipts ?? []}
              rowKey={(row) => row.id}
              activeRowKey={openReceiptId}
              onRowClick={(row) => setOpenReceiptId(row.id)}
              empty={
                <EmptyState
                  icon={Inbox}
                  title="Aucun document"
                  description="Déposez des fichiers numérisés pour alimenter ce lot."
                />
              }
            />
          </div>

          <p className="text-2xs text-ink-faint">
            Créé le {formatDateTime(lot.createdAt)}
            {lot.archivedAt ? ` · archivé le ${formatDateTime(lot.archivedAt)}` : ''}
            {lot.notes ? ` · ${lot.notes}` : ''}
            {' · '}
            <Link to="/exceptions/validation" className="text-accent-text hover:underline">
              file de validation
            </Link>
          </p>
        </div>
      </div>

      {openReceiptId && (
        <div className="anim-in fixed inset-0 z-40 bg-canvas xl:static xl:z-auto xl:w-[34rem] xl:shrink-0">
          <DocumentViewer receiptId={openReceiptId} onClose={() => setOpenReceiptId(null)} />
        </div>
      )}
    </div>
  );
}

function Metric({
  label,
  value,
  tone = 'neutral',
}: {
  label: string;
  value: string;
  tone?: Tone;
}): React.JSX.Element {
  const toneClass = {
    neutral: 'text-ink',
    accent: 'text-accent-text',
    success: 'text-success',
    warning: 'text-warning',
    danger: 'text-danger',
    info: 'text-info',
  }[tone];

  return (
    <div>
      <p className="text-2xs text-ink-faint">{label}</p>
      <p className={cn('tabular mt-0.5 text-lg leading-none font-semibold', toneClass)}>{value}</p>
    </div>
  );
}
